/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useSalesAccountHierarchy.ts
 * PURPOSE: Load public.accounts hierarchy for CRM hub account via salesAccountId bridge (hierarchy bridge)
 * PUBLIC_API: useSalesAccountHierarchy
 */

import { useQuery } from '@tanstack/react-query';
import { accountApi, type Account } from '@shared/lib/frontend/api/accountApi';

export function useSalesAccountHierarchy(
  salesAccountId: string | null | undefined,
  enabled = true
) {
  const id = salesAccountId?.trim() || null;
  const on = Boolean(enabled && id);

  const accountQuery = useQuery({
    queryKey: ['crm', 'sales-account', id],
    queryFn: () => accountApi.get(id!),
    enabled: on,
    staleTime: 60_000,
  });

  const hierarchyQuery = useQuery({
    queryKey: ['crm', 'sales-account-hierarchy', id],
    queryFn: () => accountApi.getHierarchy(id!),
    enabled: on,
    staleTime: 60_000,
  });

  const subsidiariesQuery = useQuery({
    queryKey: ['crm', 'sales-account-subsidiaries', id],
    queryFn: () => accountApi.getSubsidiaries(id!, 1, 8),
    enabled: on,
    staleTime: 60_000,
  });

  const ancestors = hierarchyQuery.data?.ancestors ?? [];
  const immediateParent: Account | null = ancestors.length > 0 ? ancestors[0]! : null;
  const subsidiaryCount =
    accountQuery.data?.subsidiary_count ?? subsidiariesQuery.data?.total ?? 0;
  const directSubsidiaries = subsidiariesQuery.data?.subsidiaries ?? [];

  return {
    salesAccount: accountQuery.data ?? null,
    immediateParent,
    ancestorChain: ancestors,
    subsidiaryCount,
    directSubsidiaries,
    loading: accountQuery.isLoading || hierarchyQuery.isLoading || subsidiariesQuery.isLoading,
    error: accountQuery.error ?? hierarchyQuery.error ?? subsidiariesQuery.error,
    refetch: async () => {
      await Promise.all([
        accountQuery.refetch(),
        hierarchyQuery.refetch(),
        subsidiariesQuery.refetch(),
      ]);
    },
  };
}
